import type * as React from "react";

import { type OrderRoute, RoutingTag } from "./RoutingTag";
import { type OrderStatus, StatusChip } from "./StatusChip";
import { TimeLabel } from "./TimeLabel";
import { cn } from "../lib/cn";

export interface OrderLineProps extends Omit<
  React.LiHTMLAttributes<HTMLLIElement>,
  "children"
> {
  status: OrderStatus;
  children: React.ReactNode;
  route?: OrderRoute;
  price?: string;
  time?: Date | string;
  /** Narrow columns: a tighter chip column and smaller text. */
  dense?: boolean;
  ref?: React.Ref<HTMLLIElement>;
}

/** Renders an `<li>`. */
export function OrderLine({
  status,
  children,
  route,
  price,
  time,
  dense = false,
  className,
  ...rest
}: OrderLineProps) {
  return (
    <li
      className={cn(
        "flex list-none items-start border-b border-rule py-[9px]",
        dense ? "gap-3" : "gap-5",
        className,
      )}
      {...rest}
    >
      <span className={cn("shrink-0 pt-[3px]", dense ? "w-24" : "w-32")}>
        <StatusChip status={status} />
      </span>
      <span
        className={cn(
          "min-w-0 flex-1 font-body leading-[1.3] text-ink",
          dense ? "text-[1.0625rem]" : "text-[1.1875rem]",
        )}
      >
        {children}
        {route && (
          <RoutingTag
            route={route}
            className="relative -top-0.5 ml-2.5 align-middle"
          />
        )}
      </span>
      {price != null && (
        <span className="min-w-[84px] shrink-0 text-right text-mono-time text-[1.0625rem] text-ink">
          {price}
        </span>
      )}
      <span className="min-w-16 shrink-0 pt-[3px] text-right">
        {time != null && <TimeLabel value={time} />}
      </span>
    </li>
  );
}
